// Admin-only removal state (migration 0027 order_removals).
// One shared, ref-counted store so AdminOrdersTab, AdminNotificationsTab,
// AdminRepsTab etc. share ONE fetch and ONE realtime channel. Reps and
// dealers never mount this (the table is admin-only under RLS).
import { useState, useEffect, useMemo } from "react";
import { listOrderRemovals, subscribeOrderRemovals, readCachedOrderRemovals, writeCachedOrderRemovals } from "./supabase";
import { currentRemovals, mergeRemovalEvents, visibleInvoices } from "./orderRemovals";
import { SHARED_INVOICES } from "./RepPortal";

let events = [];
let missingTable = false;
let loaded = false;
let snap = null;
const listeners = new Set();
let unsubRealtime = null;

function rebuild() {
  snap = { events, map: currentRemovals(events), missingTable, loaded, refresh };
}
function emit() { rebuild(); listeners.forEach(fn => { try { fn(snap); } catch (e) { console.warn("[adminOrderRemovals] listener threw:", e?.message || e); } }); }

let refreshSeq = 0;
async function refresh() {
  const mySeq = ++refreshSeq;
  const res = await listOrderRemovals();
  // A newer refresh started while this one was in flight: its result wins.
  if (mySeq !== refreshSeq) return;
  // Merge (never replace wholesale): the log is append-only, so an event that
  // arrived by realtime / local apply during the fetch must not be dropped.
  events = mergeRemovalEvents(res.events || [], events);
  missingTable = !!res.missingTable;
  loaded = true;
  emit();
}

function applyRow(row) {
  if (!row || row.id == null) return;
  if (events.some(e => e.id === row.id)) return;
  events = [...events, row];
  // Realtime events from another admin must survive a remount / failed fetch: mirror to the cache too.
  try { writeCachedOrderRemovals(events); } catch (e) { console.warn("[adminOrderRemovals] cache write failed:", e?.message || e); }
  emit();
}

function start() {
  if (unsubRealtime) return;
  // MERGE the cache into what is already in memory (never overwrite): an event applied by realtime
  // while no tab was mounted must not be lost to an older cached copy.
  try { events = mergeRemovalEvents(readCachedOrderRemovals(), events); } catch (e) { console.warn("[adminOrderRemovals] cache read failed:", e?.message || e); }
  emit();
  refresh().catch(e => console.warn("[adminOrderRemovals] initial refresh failed:", e?.message || e));
  unsubRealtime = subscribeOrderRemovals(({ event, row }) => {
    if (event === "INSERT") applyRow(row);
    else refresh().catch(e => console.warn("[adminOrderRemovals] realtime refresh failed:", e?.message || e));
  });
}
function stop() {
  if (listeners.size === 0 && unsubRealtime) { unsubRealtime(); unsubRealtime = null; }
}

rebuild();

// Call an event applier after a successful local insert so the UI updates
// without waiting for realtime.
export function applyLocalRemovalEvent(row) { applyRow(row); }

// Non-hook read for nested render helpers (pair with a useOrderRemovals()
// call higher in the component so it re-renders on change).
export function getRemovedMap() { return snap ? snap.map : {}; }

// ONE shared selector for every ADMIN-side display reader of invoices/orders:
// live SHARED_INVOICES minus currently removed ones. Re-renders on either
// change. Lookups that must still find a removed invoice (restore, detail)
// should keep using SHARED_INVOICES.getAll() directly.
export function useAdminVisibleInvoices() {
  const s = useOrderRemovals();
  const [all, setAll] = useState(() => SHARED_INVOICES.getAll());
  useEffect(() => SHARED_INVOICES.subscribe(setAll), []);
  const map = s && s.map ? s.map : null;
  return useMemo(() => visibleInvoices(all, map), [all, map]);
}

export function useOrderRemovals() {
  const [s, setS] = useState(() => snap);
  useEffect(() => {
    listeners.add(setS);
    start();
    setS(snap);
    return () => { listeners.delete(setS); stop(); };
  }, []);
  return s;
}
